import type { ExecutionPlanBatch } from '../../hooks/useExecutionPlan'
import type { EmbeddedQuestion } from '../../hooks/useAgentChat'

export interface AutoConfirmCostSettings {
  image: boolean
  video: boolean
}

export const DEFAULT_AUTO_CONFIRM_COST_SETTINGS: AutoConfirmCostSettings = {
  image: true,
  video: false,
}

const IMAGE_COST_NODE_TYPES = new Set([
  'character_designer',
  'location_designer',
  'object_designer',
  'image_generator',
  'webtoon_panel_generator',
  'storyboard_designer',
  'storyboard_no_enhance_designer',
])

const VIDEO_COST_NODE_TYPES = new Set([
  'video_generator',
])

type AutoConfirmCostClass = 'image' | 'video' | 'manual'

const AUDIO_COST_ACTIONS = new Set([
  'generate_dialogue_tts',
  'regenerate_tts_line',
  'regenerate_voice',
  'run_voice_acting',
])

function classifyCostNode(type: string, action?: string | null): AutoConfirmCostClass {
  if (action && AUDIO_COST_ACTIONS.has(action)) return 'manual'
  if (action) return 'manual'
  if (VIDEO_COST_NODE_TYPES.has(type)) return 'video'
  if (IMAGE_COST_NODE_TYPES.has(type)) return 'image'
  return 'manual'
}

function canAutoConfirmNodeTypes(
  nodes: Array<{ type: string; action?: string | null }>,
  settings: AutoConfirmCostSettings,
): boolean {
  if (nodes.length === 0) return false

  return nodes.every(({ type, action }) => {
    const costClass = classifyCostNode(type, action)
    if (costClass === 'image') return settings.image
    if (costClass === 'video') return settings.video
    return false
  })
}

export function canAutoConfirmBatch(
  batch: ExecutionPlanBatch,
  settings: AutoConfirmCostSettings,
): boolean {
  if (batch.status !== 'pending') return false
  return canAutoConfirmNodeTypes(
    batch.entries.map((entry) => ({ type: entry.type, action: entry.action })),
    settings,
  )
}

export function canAutoConfirmQuestion(
  question: EmbeddedQuestion,
  settings: AutoConfirmCostSettings,
): boolean {
  const nodes = question.metadata?.nodes?.map((node) => ({
    type: node.type ?? '',
    action: node.action,
  })) ?? []
  return canAutoConfirmNodeTypes(nodes, settings)
}
